import { Link } from 'react-router-dom';

/**
 * Service Level Agreement (SLA) / Acuerdo de Nivel de Servicio.
 *
 * Documento de protección legal del equipo técnico (devs) y de Aosenuma como
 * Encargado/Processor. Cubre:
 *   - LFPDPPP (México): Art. 3 (definiciones), 19 (medidas), 49-55 (cadena
 *     responsable→encargado), 63-69 (sanciones administrativas), CCF Art.
 *     2106 (dolo no renunciable), 2110 (daños indirectos), 2111 (caso
 *     fortuito), 2117 (hecho de tercero).
 *   - GDPR (UE): Art. 3 (alcance territorial), 28 (DPA), 29 (personnel),
 *     32 (medidas), 33-34 (brechas), 82 (indemnización solidaria), 83
 *     (multas administrativas).
 *   - Best practices SaaS B2B: cap a 12 meses de fees, AS IS, exclusiones
 *     consequential, fuerza mayor expandida post-COVID.
 *
 * Aceptación clickwrap: el inicio de sesión en cualquier subdominio
 * `*.aosenumareport.com` implica aceptación. El RPC
 * `record_session_pii_unlock(p_aviso_version='sla_v1')` deja registro en
 * `eventos_negociacion` cada vez que el usuario consume PII bajo este SLA.
 *
 * ESTADO: BORRADOR v1. Requiere validación de:
 *   1. Abogado con cédula profesional MX en derecho mercantil + protección
 *      de datos (LFPDPPP + INAI).
 *   2. Abogado con experiencia GDPR en jurisdicción del controller
 *      (España/UE) para validar Art. 28 DPA + SCCs + Schrems II + TIA.
 *   3. Penalista MX para validar mitigaciones del riesgo CPF 211 bis +
 *      Arts. 67-69 LFPDPPP que NO se pueden limitar contractualmente.
 */
export function SLA() {
  return (
    <main style={{ maxWidth: 820, margin: '0 auto', padding: '3rem 1.5rem 5rem', fontSize: 14, lineHeight: 1.65, color: 'var(--text-mid)' }}>
      <Link to="/" style={{ fontSize: 13, color: 'var(--text-dim)' }}>
        ← Volver
      </Link>

      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 30, marginTop: 16, color: 'var(--text)' }}>
        Acuerdo de Nivel de Servicio (SLA)
      </h1>
      <p style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 4 }}>
        Última actualización: 2026-05-11 · Vigente desde: 2026-05-11
      </p>

      {/* ============================================================== */}
      {/* DISCLAIMER LEGAL OBLIGATORIO                                     */}
      {/* ============================================================== */}
      <div style={{
        marginTop: 24,
        padding: '14px 18px',
        background: 'rgba(255, 200, 100, 0.08)',
        border: '1px solid rgba(255, 200, 100, 0.3)',
        borderRadius: 8,
        fontSize: 12.5,
      }}>
        <strong style={{ color: 'var(--text)' }}>⚠ Documento en revisión legal profesional</strong>
        <br />
        Este SLA es un borrador técnico vigente, redactado con apoyo de
        investigación legal automatizada (LFPDPPP MX, GDPR UE, CCF MX, best
        practices SaaS B2B internacionales). Está pendiente de validación por
        (i) abogado con cédula profesional MX en derecho mercantil y
        protección de datos personales; (ii) abogado con experiencia GDPR en
        la jurisdicción del Cliente; (iii) penalista MX para riesgo CPF
        residual. Cualquier discrepancia con la versión final aprobada se
        notificará con 15 días naturales de anticipación al Cliente.
      </div>

      {/* ============================================================== */}
      {/* 1. PARTES                                                        */}
      {/* ============================================================== */}
      <h2 style={hStyle}>1. Identificación de las partes</h2>
      <p>
        <strong>Proveedor / Encargado del Tratamiento (Processor):</strong>{' '}
        <em>aosenuma</em> (persona moral o física por confirmar registro),
        domicilio fiscal en México. Sitio oficial:{' '}
        <a href="https://aosenuma.com/" target="_blank" rel="noopener noreferrer" style={aStyle}>aosenuma.com</a>.
      </p>
      <p>
        <strong>Cliente / Responsable del Tratamiento (Controller):</strong>{' '}
        La persona moral o física que contrata el Servicio mediante orden
        de compra, contrato marco (MSA) o aceptación electrónica de este
        SLA. La identidad del Cliente se determina por instancia (tenant)
        y queda documentada en el contrato y en la bitácora interna del
        Servicio.
      </p>
      <p>
        <strong>Personnel del Encargado:</strong> Personas físicas (devs,
        técnicos, operadores) contratadas por Aosenuma para construir y
        operar técnicamente el Servicio. Actúan bajo las instrucciones
        documentadas de Aosenuma (Art. 29 GDPR; Art. 50 RLFPDPPP). NO son
        Sub-encargados independientes ni firmantes de este SLA.
      </p>
      <p>
        <strong>Sub-encargados / Sub-processors:</strong> Cloudflare Inc.,
        Supabase Inc., Mapbox Inc., y los demás proveedores listados en el
        Anexo de Sub-encargados (entregado por separado bajo NDA).
      </p>
      <p>
        <strong>Titulares / Data Subjects:</strong> Personas físicas cuyos
        datos personales se procesan en el Servicio (propietarios de
        parcelas, ejidatarios, sucesores, representantes). El Cliente es
        Responsable directo frente a los Titulares; Aosenuma asiste como
        Encargado.
      </p>

      {/* ============================================================== */}
      {/* 2. DEFINICIONES                                                  */}
      {/* ============================================================== */}
      <h2 style={hStyle}>2. Definiciones</h2>
      <ul style={ulStyle}>
        <li><strong>Servicio:</strong> plataforma SaaS de gestión territorial geoespacial multi-tenant operada por Aosenuma en los subdominios <code>*.aosenumareport.com</code>, incluyendo dashboards, captura, expedientes, visor SIG y servicios derivados.</li>
        <li><strong>PII:</strong> información personal identificable conforme al Art. 3 fracc. V de la LFPDPPP y al Art. 4(1) del GDPR.</li>
        <li><strong>Mes de Servicio:</strong> mes calendario natural en zona UTC-6 (Ciudad de México).</li>
        <li><strong>Downtime:</strong> indisponibilidad de la API HTTP principal por causa atribuible a Aosenuma medida sobre intervalos de 5 minutos, excluyendo lo enumerado en la Sección 7.</li>
        <li><strong>Uptime mensual:</strong> <code>1 − (Downtime / minutos del mes)</code>.</li>
        <li><strong>Fees:</strong> contraprestaciones netas efectivamente pagadas por el Cliente a Aosenuma durante los doce (12) meses inmediatamente anteriores al evento que origine la reclamación.</li>
        <li><strong>Datos del Cliente:</strong> contenido subido o generado por el Cliente y sus Titulares, incluyendo PII, geometrías parcelarias y expedientes.</li>
      </ul>

      {/* ============================================================== */}
      {/* 3. ROLES BAJO LFPDPPP Y GDPR                                     */}
      {/* ============================================================== */}
      <h2 style={hStyle}>3. Roles bajo LFPDPPP y GDPR</h2>
      <p>
        Para efectos de la <strong>Ley Federal de Protección de Datos
        Personales en Posesión de los Particulares (LFPDPPP, México)</strong>:
      </p>
      <ul style={ulStyle}>
        <li>El <strong>Cliente</strong> es <em>Responsable</em> del tratamiento (Art. 3 fracc. XIV) — decide finalidades y medios.</li>
        <li><strong>Aosenuma</strong> es <em>Encargado</em> (Art. 3 fracc. IX) — procesa por cuenta del Responsable bajo instrucciones documentadas.</li>
        <li>Los <em>Sub-encargados</em> (Cloudflare, Supabase, Mapbox) operan bajo cadena de Art. 50 RLFPDPPP, con aviso al Responsable y con compromisos contractuales equivalentes.</li>
      </ul>
      <p>
        Para efectos del{' '}
        <strong>Reglamento General de Protección de Datos (GDPR, UE 2016/679)</strong>,
        cuando aplique por nexo del Cliente (Art. 3(1) GDPR):
      </p>
      <ul style={ulStyle}>
        <li>El Cliente es <em>Controller</em>.</li>
        <li>Aosenuma es <em>Processor</em>. El presente SLA constituye el Data Processing Agreement (DPA) requerido por el Art. 28(3) GDPR, complementado por Anexo Técnico de Medidas de Seguridad.</li>
        <li>Los devs / Personnel del Encargado actúan bajo el Art. 29 GDPR (procesan solo bajo instrucciones documentadas) y Art. 32(4) (obligación de confidencialidad). NO son Sub-processors independientes.</li>
      </ul>

      {/* ============================================================== */}
      {/* 4. DESCRIPCIÓN DEL SERVICIO                                      */}
      {/* ============================================================== */}
      <h2 style={hStyle}>4. Descripción del Servicio</h2>
      <p>
        El Servicio es una plataforma SaaS B2B multi-tenant que permite al
        Cliente y a sus Titulares:
      </p>
      <ul style={ulStyle}>
        <li>Visualizar y editar capas geoespaciales (parcelas, polígonos, AOIs).</li>
        <li>Capturar datos de gestión territorial, contratos, irregularidades y expedientes.</li>
        <li>Almacenar archivos cifrados en buckets de objetos (Cloudflare R2 o equivalentes).</li>
        <li>Cifrar PII en reposo con AES-256-GCM y AAD anclada al propietario_id, con descifrado solo bajo AAL2 (MFA fresco) y registro inmutable de cada lectura.</li>
        <li>Generar reportes PDF/KMZ exportables con watermark forense.</li>
      </ul>
      <p>
        El alcance funcional específico contratado se establece en la orden
        de compra correspondiente. Características en <em>beta</em>,{' '}
        <em>preview</em> o etiquetadas como experimentales se prestan sin
        SLA y pueden discontinuarse sin previo aviso.
      </p>

      {/* ============================================================== */}
      {/* 5. NIVELES DE SERVICIO                                           */}
      {/* ============================================================== */}
      <h2 style={hStyle}>5. Niveles de Servicio</h2>
      <p>
        <strong>Uptime objetivo mensual:</strong> 99.5% medido sobre la API
        principal. Aosenuma se reserva el derecho de definir un objetivo
        distinto por tier de contratación.
      </p>
      <p>
        <strong>Tiempos de respuesta de soporte</strong> (días hábiles,
        zona UTC-6):
      </p>
      <ul style={ulStyle}>
        <li>Severidad 1 (caída total / pérdida de datos): respuesta en ≤ 4 horas hábiles.</li>
        <li>Severidad 2 (degradación funcional): respuesta en ≤ 1 día hábil.</li>
        <li>Severidad 3 (consulta / mejora): respuesta en ≤ 3 días hábiles.</li>
      </ul>
      <p>
        Estos tiempos miden la <em>respuesta inicial</em>, no la resolución.
        El compromiso de resolución se evalúa de buena fe según el alcance,
        complejidad y dependencias externas.
      </p>

      {/* ============================================================== */}
      {/* 6. CRÉDITOS DE SERVICIO                                          */}
      {/* ============================================================== */}
      <h2 style={hStyle}>6. Créditos de Servicio</h2>
      <p>
        Si el Uptime mensual cae por debajo del objetivo, el Cliente puede
        solicitar por escrito un crédito calculado así:
      </p>
      <ul style={ulStyle}>
        <li>Uptime entre 99.0% y 99.5%: crédito del 5% del fee mensual.</li>
        <li>Uptime entre 95.0% y 99.0%: crédito del 10%.</li>
        <li>Uptime menor a 95.0%: crédito del 20%.</li>
      </ul>
      <p style={importantStyle}>
        Los créditos de servicio son el <strong>único y exclusivo remedio</strong>
        {' '}del Cliente por incumplimiento del objetivo de Uptime,{' '}
        <strong>sin perjuicio de la Sección 13</strong> (cláusulas no
        limitables: dolo, culpa grave, derechos de Titulares, sanciones de
        autoridad). El Cliente renuncia expresamente a cualquier otra
        reclamación monetaria, contractual o extracontractual derivada del
        Downtime ordinario.
      </p>
      <p>
        La solicitud de crédito debe presentarse en los <strong>30 días naturales</strong>
        {' '}siguientes al cierre del Mes de Servicio afectado. Vencido ese
        plazo, el derecho al crédito se considera renunciado.
      </p>

      {/* ============================================================== */}
      {/* 7. EXCLUSIONES                                                   */}
      {/* ============================================================== */}
      <h2 style={hStyle}>7. Exclusiones del SLA</h2>
      <p>No constituyen Downtime ni dan derecho a crédito:</p>
      <ul style={ulStyle}>
        <li>Mantenimiento programado anunciado con al menos 48 horas de anticipación.</li>
        <li>Mantenimiento de emergencia para mitigar incidentes de seguridad o cumplir con obligaciones legales (notificación post-hoc).</li>
        <li>Caídas, errores o degradaciones de proveedores upstream (Cloudflare, Supabase, AWS, Mapbox, redes de tránsito BGP).</li>
        <li>Ataques de denegación de servicio (DDoS), ransomware, o ciberataques masivos no provocados por Aosenuma.</li>
        <li>Errores del Cliente: credenciales filtradas, contenido violatorio de políticas, uso indebido del Servicio.</li>
        <li>Bloqueo regulatorio o acción gubernamental que impida el funcionamiento del Servicio.</li>
        <li>Pandemias, desastres naturales, conflictos armados, huelgas generales o cualquier evento de fuerza mayor conforme a la Sección 15.</li>
        <li>Beta features, preview features, y APIs marcadas como experimentales.</li>
      </ul>

      {/* ============================================================== */}
      {/* 8. MEDIDAS DE SEGURIDAD                                          */}
      {/* ============================================================== */}
      <h2 style={hStyle}>8. Medidas de Seguridad</h2>
      <p>
        Conforme al Art. 19 LFPDPPP y al Art. 32 GDPR, Aosenuma implementa
        medidas administrativas, técnicas y físicas <strong>razonables y
        proporcionales al riesgo</strong>, incluyendo de forma enunciativa
        más no limitativa:
      </p>
      <ul style={ulStyle}>
        <li>Cifrado de PII en reposo con AES-256-GCM y datos asociados (AAD) anclados al identificador del Titular.</li>
        <li>TLS 1.2+ en tránsito (HTTPS/WSS).</li>
        <li>MFA obligatorio (TOTP) para todos los roles autenticados; descifrado de PII condicionado a AAL2 fresco con TTL de 8 horas.</li>
        <li>Row-Level Security (RLS) multi-tenant estricta en Postgres.</li>
        <li>Bitácora inmutable (append-only) de toda lectura/exportación de PII.</li>
        <li>Captura y reporte de errores con Sentry, con redacción automática de PII en stack traces.</li>
        <li>Política de mínimo privilegio para Personnel.</li>
        <li>Backups cifrados con retención conforme al tier contratado.</li>
      </ul>
      <p style={importantStyle}>
        Aosenuma <strong>NO garantiza ausencia absoluta de brechas</strong>.
        Ningún sistema de información es 100% seguro. El Servicio se ofrece
        con medidas estándar de la industria; no se prometen niveles de
        seguridad imposibles de cumplir.
      </p>

      {/* ============================================================== */}
      {/* 9. NOTIFICACIÓN DE BRECHAS                                       */}
      {/* ============================================================== */}
      <h2 style={hStyle}>9. Notificación de brechas</h2>
      <p>
        En caso de incidente de seguridad que afecte PII (Art. 20 LFPDPPP,
        Art. 33 GDPR), Aosenuma notificará por escrito al Cliente{' '}
        <strong>sin dilación indebida</strong>, idealmente dentro de las
        primeras <strong>48 horas</strong> desde el conocimiento confirmado,
        con la información disponible al momento (naturaleza, alcance
        estimado, medidas en curso). El Cliente es responsable de la
        notificación posterior a Titulares, INAI y/o AEPD en su carácter de
        Responsable / Controller, conforme a las leyes aplicables.
      </p>
      <p>
        Aosenuma cooperará razonablemente con el Cliente para evaluar
        impacto, contener el incidente y cumplir las obligaciones de
        notificación. Los costos de notificación, defensa legal y
        remediación corresponderán a quien resulte responsable según la
        causa raíz, sin perjuicio de las limitaciones de la Sección 12.
      </p>

      {/* ============================================================== */}
      {/* 10. DEPENDENCIAS DE TERCEROS                                     */}
      {/* ============================================================== */}
      <h2 style={hStyle}>10. Dependencias de terceros</h2>
      <p>
        El Cliente <strong>reconoce expresamente</strong> que el Servicio se
        apoya en proveedores upstream para infraestructura, almacenamiento,
        autenticación, mapas base, monitoreo y entrega. La lista actualizada
        de Sub-encargados se mantiene en el Anexo correspondiente. Los
        sub-encargados principales incluyen, sin limitación:
      </p>
      <ul style={ulStyle}>
        <li><strong>Cloudflare Inc.</strong> (CDN, WAF, Workers, R2 — almacenamiento de objetos cifrados).</li>
        <li><strong>Supabase Inc.</strong> (Postgres + Auth + Realtime). Datos físicos en AWS us-east-1.</li>
        <li><strong>Mapbox Inc.</strong> (tiles de mapa base, geocodificación).</li>
        <li><strong>Google Fonts</strong> (fuentes web).</li>
      </ul>
      <p style={importantStyle}>
        Conforme al Art. 28(4) GDPR (cuando aplique) y al Art. 50 RLFPDPPP,
        Aosenuma responde frente al Cliente por el cumplimiento de
        obligaciones de protección de datos de sus Sub-encargados como si
        fueran propias (<strong>responsabilidad subrogada</strong>). Sin
        perjuicio de lo anterior, las fallas operativas, caídas o
        decisiones unilaterales de los Sub-encargados que escapan al
        control razonable de Aosenuma quedan cubiertas por la Sección 7
        (Exclusiones) y la Sección 15 (Fuerza Mayor). Aosenuma elige
        proveedores razonables, mantiene compromisos contractuales con
        ellos (DPAs, SCCs cuando aplique) y migrará a alternativas
        comparables si un proveedor deja de cumplir estándares aceptables.
      </p>

      {/* ============================================================== */}
      {/* 11. DISCLAIMERS                                                  */}
      {/* ============================================================== */}
      <h2 style={hStyle}>11. Garantías y Disclaimers</h2>
      <div style={{
        marginTop: 12,
        padding: '12px 16px',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 8,
        fontSize: 12.5,
        fontFamily: 'monospace',
        textTransform: 'uppercase',
        letterSpacing: '0.02em',
        lineHeight: 1.7,
      }}>
        EL SERVICIO SE PROPORCIONA "TAL COMO ESTÁ" Y "SEGÚN DISPONIBILIDAD",
        SIN GARANTÍAS EXPRESAS NI IMPLÍCITAS DE NINGÚN TIPO, INCLUYENDO,
        SIN LIMITACIÓN, GARANTÍAS DE COMERCIABILIDAD, IDONEIDAD PARA UN
        PROPÓSITO PARTICULAR, EXACTITUD, COMPLETITUD, NO INFRACCIÓN, O
        AUSENCIA DE ERRORES. NINGUNA INFORMACIÓN, ORAL O ESCRITA, OBTENIDA
        POR EL CLIENTE DE AOSENUMA O A TRAVÉS DEL SERVICIO CREARÁ GARANTÍA
        ALGUNA NO ESTABLECIDA EXPRESAMENTE EN ESTE SLA.
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 8 }}>
        Disclaimer en mayúsculas y monospace conforme a la práctica
        internacional de SaaS B2B para asegurar que sea visible y aceptado
        de forma informada por el Cliente — sujeto siempre a las
        limitaciones de orden público bajo derecho mexicano (Art. 2106 CCF
        en materia de dolo) y a la naturaleza B2B de este contrato (no
        aplica la Ley Federal de Protección al Consumidor).
      </p>

      {/* ============================================================== */}
      {/* 12. LIMITACIÓN DE RESPONSABILIDAD                                */}
      {/* ============================================================== */}
      <h2 style={hStyle}>12. Limitación de Responsabilidad</h2>
      <p>
        En la máxima medida permitida por la ley aplicable:
      </p>
      <ul style={ulStyle}>
        <li>
          La responsabilidad <strong>agregada total</strong> de Aosenuma frente
          al Cliente por cualquier reclamación, contractual o extracontractual,
          relacionada con el Servicio, NO excederá la cantidad equivalente a
          los <strong>Fees efectivamente pagados</strong> por el Cliente a
          Aosenuma durante los <strong>doce (12) meses inmediatamente
          anteriores</strong> al evento que origine la reclamación (Art. 2110
          CCF: indemnización por daños directos e inmediatos exigible entre
          comerciantes).
        </li>
        <li>
          Aosenuma <strong>NO responde por daños indirectos, especiales,
          incidentales, consecuenciales o punitivos</strong>, incluyendo
          lucro cesante, pérdida de datos, pérdida de oportunidad de negocio,
          daño reputacional, costos de procuración de servicios sustitutos,
          ni interrupción de operaciones (Art. 2110 CCF; aplicable también
          bajo Art. 5(2) Cód. Comercio MX y práctica internacional de SaaS
          B2B).
        </li>
        <li>
          El cap aplica de forma <strong>agregada</strong> sobre todas las
          reclamaciones combinadas, NO por incidente individual.
        </li>
        <li>
          Toda reclamación debe presentarse por escrito a Aosenuma dentro
          de los <strong>doce (12) meses</strong> siguientes al evento que
          la origine. Vencido ese plazo, el derecho a reclamar se considera
          renunciado.
        </li>
      </ul>
      <p>
        El Cliente <strong>indemniza y mantiene libre de daño</strong> a
        Aosenuma, sus afiliadas y sus Personnel por reclamaciones de
        terceros derivadas de: (a) Datos del Cliente que infrinjan
        derechos de terceros; (b) uso del Servicio en violación de leyes
        aplicables, del presente SLA o de políticas de uso aceptable;
        (c) decisiones de negocio del Cliente basadas en outputs del
        Servicio; (d) credenciales del Cliente comprometidas por descuido
        del propio Cliente. Los Sub-encargados no son terceros
        beneficiarios de esta indemnización; ellos mantienen sus propios
        DPAs con sus respectivos clientes.
      </p>

      {/* ============================================================== */}
      {/* 13. CLÁUSULAS NO LIMITABLES                                      */}
      {/* ============================================================== */}
      <h2 style={hStyle}>13. Cláusulas no limitables (orden público)</h2>
      <p>
        Ninguna parte de este SLA pretende limitar, ni se interpretará como
        limitando, responsabilidad por:
      </p>
      <ul style={ulStyle}>
        <li><strong>Dolo o mala fe</strong> (Art. 2106 CCF: la renuncia anticipada es nula de pleno derecho).</li>
        <li><strong>Culpa grave</strong> (jurisprudencia mercantil MX la equipara a dolo).</li>
        <li><strong>Daño a la integridad física o vida</strong> de personas (orden público; aplicable bajo Cód. Penal Federal y equivalentes UE).</li>
        <li><strong>Sanciones administrativas firmes de INAI</strong> al Responsable (Arts. 63-65 LFPDPPP) y multas administrativas del Art. 83 GDPR cuando aplique, en lo que corresponde al rol de cada parte.</li>
        <li><strong>Derechos ARCO de los Titulares</strong> (Arts. 22-37 LFPDPPP) y derechos del data subject bajo Capítulo III del GDPR. Estos derechos son irrenunciables por los Titulares y de orden público.</li>
        <li><strong>Indemnización del data subject</strong> bajo Art. 82 GDPR, donde Controller y Processor pueden tener responsabilidad solidaria frente al Titular.</li>
        <li><strong>Obligaciones de pago vencidas</strong> del Cliente a Aosenuma.</li>
      </ul>

      {/* ============================================================== */}
      {/* 14. INDEMNIZACIÓN MUTUA                                          */}
      {/* ============================================================== */}
      <h2 style={hStyle}>14. Indemnización mutua</h2>
      <p>
        Cada parte indemnizará y mantendrá libre de daño a la otra por
        reclamaciones de terceros derivadas de:
      </p>
      <ul style={ulStyle}>
        <li><strong>Aosenuma indemniza al Cliente</strong> por reclamaciones de propiedad intelectual de terceros que afirmen que el Servicio en sí mismo (excluyendo Datos del Cliente y Sub-encargados de terceros) infringe sus derechos. Esta indemnización por IP NO está sujeta al cap de la Sección 12 ("super-cap"): se rige por el monto razonable de defensa y resolución, hasta un máximo agregado equivalente a dos (2) veces los Fees pagados en los doce (12) meses anteriores. Práctica estándar SaaS B2B.</li>
        <li><strong>El Cliente indemniza a Aosenuma</strong> por reclamaciones de terceros derivadas de los Datos del Cliente, uso indebido del Servicio, o incumplimiento del Cliente con leyes aplicables.</li>
      </ul>
      <p>
        La parte que reciba notificación de reclamación deberá comunicarla
        a la otra por escrito en ≤ 30 días naturales y cooperar
        razonablemente en la defensa.
      </p>

      {/* ============================================================== */}
      {/* 15. FUERZA MAYOR                                                 */}
      {/* ============================================================== */}
      <h2 style={hStyle}>15. Fuerza Mayor</h2>
      <p>
        Conforme al Art. 2111 CCF, ninguna parte responde por incumplimientos
        causados por eventos fuera de su control razonable, incluyendo de
        forma enunciativa más no limitativa:
      </p>
      <ul style={ulStyle}>
        <li>Pandemias, epidemias y emergencias sanitarias declaradas por OMS o autoridades equivalentes.</li>
        <li>Desastres naturales: sismos, huracanes, inundaciones, erupciones, incendios forestales.</li>
        <li>Conflictos armados, terrorismo, motines, sabotaje, embargo comercial.</li>
        <li>Acciones gubernamentales o regulatorias que impidan la operación del Servicio.</li>
        <li>Cyberataques masivos no provocados (DDoS, ransomware en cascada, ataques a infraestructura DNS).</li>
        <li>Caída regional sostenida de proveedores upstream críticos (AWS region, Cloudflare backbone, ISP de tránsito).</li>
        <li>Huelgas generales o falla de servicios públicos esenciales.</li>
      </ul>
      <p>
        La parte afectada notificará la fuerza mayor por escrito dentro de
        las 72 horas siguientes y hará esfuerzos razonables para mitigar el
        impacto. Si el evento persiste más de 30 días naturales, cualquiera
        de las partes puede dar por terminado el contrato sin penalidad.
      </p>

      {/* ============================================================== */}
      {/* 16. OBLIGACIONES DEL CLIENTE                                     */}
      {/* ============================================================== */}
      <h2 style={hStyle}>16. Obligaciones del Cliente</h2>
      <p>El Cliente se obliga a:</p>
      <ul style={ulStyle}>
        <li>Mantener la confidencialidad de las credenciales de sus usuarios y configurar MFA donde el Servicio lo ofrezca.</li>
        <li>Usar el Servicio conforme a las leyes aplicables, incluyendo LFPDPPP, GDPR cuando aplique, y normas sectoriales mexicanas (Ley Agraria, etc.).</li>
        <li>Obtener el consentimiento informado de los Titulares cuando la ley lo requiera, y publicar el Aviso de Privacidad correspondiente.</li>
        <li>Atender las solicitudes de derechos ARCO de los Titulares; Aosenuma asistirá con la información técnica disponible en plazos razonables.</li>
        <li>Pagar los Fees acordados dentro de los plazos pactados.</li>
        <li>No realizar ingeniería inversa del Servicio salvo lo permitido por ley imperativa.</li>
        <li>No usar el Servicio para actividades ilícitas o que infrinjan derechos de terceros.</li>
      </ul>

      {/* ============================================================== */}
      {/* 17. ACEPTACIÓN IMPLÍCITA (CLICKWRAP)                             */}
      {/* ============================================================== */}
      <h2 style={hStyle}>17. Aceptación implícita por inicio de sesión</h2>
      <p>
        El <strong>inicio de sesión</strong> en cualquier subdominio del
        Servicio operado por Aosenuma, por parte de un usuario autorizado
        por el Cliente, constituye <strong>aceptación electrónica</strong>{' '}
        del presente SLA en la versión vigente al momento del inicio.
      </p>
      <p>
        Cada vez que el usuario verifica un código TOTP para desbloquear
        visualización de PII, el sistema deja registro en la bitácora
        (tabla <code>eventos_negociacion</code>, evento{' '}
        <code>session.pii_unlock_granted</code>) con la versión vigente del
        SLA al momento, timestamp UTC, rol del usuario y user_id. Este
        registro constituye <strong>evidencia operativa de la aceptación
        contractual del usuario operador</strong> respecto del SLA. NO
        sustituye el consentimiento informado del Titular (Art. 8 LFPDPPP /
        Art. 7 GDPR), que corresponde recabar al Cliente como Responsable
        directamente del Titular antes del tratamiento, mediante su propio
        Aviso de Privacidad.
      </p>

      {/* ============================================================== */}
      {/* 18. TRANSFERENCIAS INTERNACIONALES                               */}
      {/* ============================================================== */}
      <h2 style={hStyle}>18. Transferencias internacionales de datos</h2>
      <p>
        Los datos pueden procesarse en infraestructura ubicada en Estados
        Unidos (Supabase: AWS us-east-1; Cloudflare: red global; Mapbox: US)
        y México (Personnel de Aosenuma). Cuando aplique GDPR por nexo del
        Cliente:
      </p>
      <ul style={ulStyle}>
        <li>México no cuenta a la fecha con decisión de adecuación de la Comisión Europea. Las transferencias Cliente → Aosenuma se respaldan en las <strong>Standard Contractual Clauses (SCC) UE 2021/914 Módulo 2</strong> (controller-to-processor) firmadas por separado.</li>
        <li>Para Sub-encargados US (Supabase, Cloudflare, Mapbox), Aosenuma verifica si están certificados bajo el <strong>EU-US Data Privacy Framework (DPF)</strong>. De no estarlo, se aplican SCCs Módulo 3 (processor-to-processor) y se realiza un Transfer Impact Assessment (TIA).</li>
        <li>El Cliente, como Controller, conserva la responsabilidad primaria del TIA; Aosenuma aporta la información técnica necesaria sobre cifrado, gobernanza de claves y leyes mexicanas de acceso gubernamental a datos.</li>
      </ul>

      {/* ============================================================== */}
      {/* 19. PERSONNEL DEL ENCARGADO                                      */}
      {/* ============================================================== */}
      <h2 style={hStyle}>19. Personnel del Encargado</h2>
      <p>
        Los desarrolladores y técnicos contratados por Aosenuma para
        construir y operar el Servicio actúan exclusivamente como{' '}
        <strong>Personnel del Encargado</strong> (Art. 29 GDPR; Art. 50
        RLFPDPPP) y NO como Sub-encargados independientes. En consecuencia:
      </p>
      <ul style={ulStyle}>
        <li>Procesan datos solo bajo instrucciones documentadas de Aosenuma.</li>
        <li>Están sujetos a obligación de confidencialidad bajo NDA contractual y bajo Art. 32(4) GDPR.</li>
        <li>NO firman este SLA ni cualquier otro contrato directamente con el Cliente o los Titulares.</li>
        <li>El Cliente acuerda canalizar TODA reclamación operativa o legal exclusivamente a través de Aosenuma. Reclamaciones directas a Personnel individuales son improcedentes.</li>
        <li>Aosenuma asume frente al Cliente la responsabilidad por las acciones u omisiones de sus Personnel actuando dentro del alcance de sus funciones, sujeto a las limitaciones de la Sección 12.</li>
      </ul>
      <p style={importantStyle}>
        Esta cláusula NO limita el riesgo penal personal que las leyes
        impongan directamente sobre personas físicas (Arts. 67-69 LFPDPPP;
        Arts. 211 bis 1-6 Cód. Penal Federal; equivalentes en otras
        jurisdicciones). El riesgo penal no es contractualmente delegable.
      </p>

      {/* ============================================================== */}
      {/* 20. TERMINACIÓN Y SUPERVIVENCIA                                  */}
      {/* ============================================================== */}
      <h2 style={hStyle}>20. Terminación y supervivencia</h2>
      <p>
        Este SLA se mantiene vigente mientras exista una orden de compra o
        contrato marco activo entre las partes. Cualquiera de las partes
        puede dar por terminado el contrato:
      </p>
      <ul style={ulStyle}>
        <li>Por mutuo acuerdo en cualquier momento.</li>
        <li>Por incumplimiento material no subsanado dentro de 30 días naturales tras notificación por escrito.</li>
        <li>Por insolvencia, concurso mercantil o cese de operaciones de la otra parte.</li>
        <li>Por fuerza mayor prolongada conforme a la Sección 15.</li>
      </ul>
      <p>
        Tras la terminación, Aosenuma entregará al Cliente una exportación
        de los Datos del Cliente en formato razonable dentro de 30 días
        naturales y eliminará los datos restantes dentro de 90 días
        adicionales, salvo retención legal obligatoria. Las siguientes
        secciones <strong>sobreviven a la terminación</strong>:
        Definiciones, Sección 8 (Seguridad en lo aplicable a datos
        residuales), Sección 9 (Brechas en lo aplicable a datos retenidos),
        Sección 12 (Limitación de Responsabilidad), Sección 13 (No
        limitables), Sección 14 (Indemnización), Sección 19 (Personnel) y
        Secciones 22-24 (legales).
      </p>

      {/* ============================================================== */}
      {/* 21. CUMPLIMIENTO EXTRANJERO                                      */}
      {/* ============================================================== */}
      <h2 style={hStyle}>21. Cumplimiento con jurisdicciones extranjeras</h2>
      <p>
        El Cliente es responsable de evaluar la idoneidad del Servicio
        respecto a las leyes de cada jurisdicción donde opere. Aosenuma no
        garantiza cumplimiento con regulaciones sectoriales específicas
        fuera de México (e.g., HIPAA US, PIPEDA Canadá, LGPD Brasil, PIPL
        China) salvo que se acuerde por escrito en una orden de compra
        complementaria.
      </p>

      {/* ============================================================== */}
      {/* 22. LEY APLICABLE Y JURISDICCIÓN                                 */}
      {/* ============================================================== */}
      <h2 style={hStyle}>22. Ley aplicable, arbitraje y jurisdicción</h2>
      <p>
        Este SLA se rige por las leyes federales mexicanas, en particular
        el Código Civil Federal, el Código de Comercio, la LFPDPPP y su
        Reglamento. Cuando aplique GDPR por nexo del Cliente, las
        provisiones imperativas del GDPR prevalecen sobre cualquier cláusula
        contraria.
      </p>
      <p>
        Cualquier controversia derivada del presente SLA se resolverá
        mediante <strong>arbitraje comercial</strong> conforme al Reglamento
        de la <strong>Cámara Nacional de Comercio de la Ciudad de México
        (CANACO)</strong> o de la <strong>Cámara de Arbitraje de México
        (CAM)</strong>, en idioma español, con sede en Ciudad de México y
        un (1) árbitro. El laudo será definitivo y ejecutable en cualquier
        jurisdicción competente conforme a la Convención de Nueva York.
      </p>
      <p>
        Lo anterior sin perjuicio del derecho de cualquier parte de
        solicitar medidas cautelares en tribunales competentes y del
        derecho de los Titulares de ejercer acciones ante INAI, AEPD u
        otras autoridades de protección de datos aplicables, conforme a las
        leyes de orden público.
      </p>

      {/* ============================================================== */}
      {/* 23. MODIFICACIONES                                               */}
      {/* ============================================================== */}
      <h2 style={hStyle}>23. Modificaciones al SLA</h2>
      <p>
        Aosenuma puede actualizar este SLA notificando al Cliente con al
        menos <strong>30 días naturales de anticipación</strong> a través
        del correo de contacto registrado, banner en el Servicio, o
        publicación en la URL pública del SLA. Las modificaciones materiales
        que reduzcan los derechos del Cliente
        requieren su aceptación expresa; en caso de objeción de buena fe,
        el Cliente puede dar por terminado el contrato conforme a la
        Sección 20 sin penalidad y con devolución pro-rata de Fees no
        consumidos.
      </p>

      {/* ============================================================== */}
      {/* 24. DOCUMENTOS RELACIONADOS                                      */}
      {/* ============================================================== */}
      <h2 style={hStyle}>24. Documentos relacionados</h2>
      <p>
        El presente SLA forma parte de un conjunto contractual completo
        que incluye:
      </p>
      <ul style={ulStyle}>
        <li>Orden de compra o contrato marco (MSA) firmado con el Cliente.</li>
        <li>Anexo Técnico de Medidas de Seguridad — descripción detallada de controles administrativos, técnicos y físicos. Entregado bajo NDA por separado.</li>
        <li>Anexo de Sub-encargados — lista actualizada de proveedores upstream con sus DPAs y certificaciones. Entregado bajo NDA por separado.</li>
        <li>Standard Contractual Clauses UE 2021/914 — firmadas por separado cuando aplique GDPR.</li>
        <li>Data Processing Agreement (DPA) específico cuando lo requiera la jurisdicción del Cliente.</li>
      </ul>
      <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 8 }}>
        El código fuente, diseños, marcas, modelos de datos y agregados
        derivados del Servicio son <strong>propiedad intelectual exclusiva
        de Aosenuma</strong>. No constituyen software de código abierto;
        el Cliente recibe únicamente una licencia de uso durante la vigencia
        del contrato. Cualquier acceso, inspección o auditoría de
        componentes técnicos se realiza bajo NDA específico y nunca
        constituye transferencia de propiedad.
      </p>

      {/* ============================================================== */}
      {/* 25. GAPS RECONOCIDOS                                             */}
      {/* ============================================================== */}
      <h2 style={hStyle}>25. Gaps reconocidos (camino a versión definitiva)</h2>
      <p>
        Aosenuma reconoce que este borrador requiere las siguientes adiciones
        para ser una base contractual completa, y se compromete a
        incorporarlas antes de la versión definitiva validada por abogado:
      </p>
      <ul style={ulStyle}>
        <li>Identificación legal completa de Aosenuma (razón social, RFC, domicilio fiscal, representante legal).</li>
        <li>SCCs UE 2021/914 Módulo 2 firmadas y adjuntas (Anexos I/II/III completos) para transferencias UE→MX, cuando aplique GDPR.</li>
        <li>Acceptance flow expreso (checkbox + click) la primera vez por cada Cliente y por cada usuario operador, capturando versión SLA, IP, timestamp.</li>
        <li>Anexo Técnico de Medidas de Seguridad detallado (SOC 2 / ISO 27001 cuando aplique).</li>
        <li>Anexo de Sub-encargados con lista cerrada, certificaciones DPF/SCCs y derecho del Cliente a objetar nuevos sub-encargados.</li>
        <li>Audit rights del Cliente (Art. 28(3)(h) GDPR): inspección con preaviso razonable y reportes de cumplimiento periódicos.</li>
        <li>DPIA assistance: deber del Encargado de asistir en la Evaluación de Impacto de Protección de Datos (Art. 35 GDPR) que corresponde al Cliente como Controller.</li>
        <li>Designación formal de DPO o Responsable de Protección de Datos cuando aplique (Art. 37 GDPR / RLFPDPPP).</li>
        <li>Política de retención por categoría de dato (PII fiscal MX 7 años, logs operativos 12 meses, backups 35 días, bitácora inmutable indefinida).</li>
        <li>Exit data plan detallado: formato, plazo, costo, attestation firmada de eliminación.</li>
        <li>Política de uso aceptable (AUP) enlazada y vinculante.</li>
        <li>Cobertura de seguro de responsabilidad civil profesional y cyber liability (≥1M USD recomendado para deals enterprise).</li>
        <li>Régimen formal de notificaciones (direcciones legales, plazos, formato).</li>
        <li>Cláusula de cesión / change of control con consentimiento del Cliente.</li>
        <li>Cláusula anti-soborno (FCPA US / UK Bribery Act / Ley General Anticorrupción MX).</li>
        <li>Cláusula de no-discriminación y no-retaliation frente a Titulares que ejerzan derechos ARCO.</li>
        <li>Propiedad intelectual del Servicio: Aosenuma retiene IP del código, marcas y agregados anonimizados; licencia de uso al Cliente durante la vigencia.</li>
        <li>Licencia perpetua a Aosenuma sobre Feedback/sugerencias enviadas por el Cliente.</li>
        <li>Cumplimiento de la Directiva ePrivacy 2002/58/CE + LSSI-CE para Cliente UE (cookies, comunicaciones electrónicas).</li>
      </ul>
      <p style={importantStyle}>
        Hasta que estos gaps se cierren, este SLA debe entenderse como un{' '}
        <strong>borrador técnico orientativo</strong> publicado con
        propósito de transparencia. Las relaciones contractuales reales con
        Clientes se rigen por la orden de compra y, cuando aplique, el DPA
        firmado por separado.
      </p>

      {/* ============================================================== */}
      {/* CIERRE                                                           */}
      {/* ============================================================== */}
      <hr style={{ marginTop: 40, border: 'none', borderTop: '1px solid rgba(255,255,255,0.08)' }} />
      <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 16 }}>
        Documento elaborado con asistencia de investigación legal automatizada.
        Fuentes citadas: LFPDPPP MX (DOF 05/07/2010 y reformas), RLFPDPPP
        (DOF 21/12/2011), Código Civil Federal MX, Código de Comercio MX,
        Código Penal Federal MX, Reglamento (UE) 2016/679 (GDPR), SCC UE
        2021/914, Convención de Nueva York 1958. Validación legal profesional
        pendiente conforme al disclaimer del inicio.
      </p>
      <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 8 }}>
        Sitio oficial:{' '}
        <a href="https://aosenuma.com/" target="_blank" rel="noopener noreferrer" style={aStyle}>aosenuma.com</a>
      </p>
    </main>
  );
}

const hStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 18,
  marginTop: 32,
  marginBottom: 8,
  color: 'var(--text)',
};

const aStyle: React.CSSProperties = {
  color: 'var(--btn-primary-bg, #b4c96f)',
  textDecoration: 'underline',
};

const ulStyle: React.CSSProperties = {
  paddingLeft: 22,
  margin: '8px 0',
};

const importantStyle: React.CSSProperties = {
  padding: '10px 14px',
  marginTop: 12,
  background: 'rgba(255, 200, 100, 0.05)',
  borderLeft: '3px solid rgba(255, 200, 100, 0.4)',
  borderRadius: 4,
  fontSize: 13,
};
